// A bundle add can fire one 'rebuy:cart.add' per item; wait for them to settle
const REBUY_ADD_SETTLE_MS = 300;

/**
 * Cart drawer — owns its open/close state (no Alpine), opens after
 * add-to-cart, and re-renders its wrapper from the section HTML that cart.js
 * bundles into every cart mutation ('cart:change' event).
 * @extends HTMLElement
 */
export class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onCartChange = this.onCartChange.bind(this);
    this.onRebuyAdd = this.onRebuyAdd.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.addEventListener('click', this.onInnerClick);
  }

  connectedCallback() {
    document.addEventListener('cart:change', this.onCartChange);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('rebuy:cart.add', this.onRebuyAdd);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:change', this.onCartChange);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('rebuy:cart.add', this.onRebuyAdd);
    window.clearTimeout(this.rebuyAddTimer);
    document.body.classList.remove('overflow-hidden');
  }

  get sectionId() {
    return this.closest('.shopify-section')?.id.replace('shopify-section-', '');
  }

  open() {
    this.returnFocusTo = null;
    this.classList.add('cart-open');
    this.querySelector('.drawer__wrapper')?.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    this.animateShippingProgress('0%');
  }

  close() {
    this.classList.remove('cart-open');
    this.querySelector('.drawer__wrapper')?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');

    // Set when the drawer took focus from its opener (a Rebuy add); hand it back
    if (this.returnFocusTo?.isConnected) {
      this.returnFocusTo.focus();
    }
    this.returnFocusTo = null;
  }

  toggle() {
    if (this.classList.contains('cart-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  onDocumentClick(event) {
    const bubble = event.target.closest('#header-cart-bubble, [data-cart-trigger]');
    if (bubble) {
      event.preventDefault();
      this.toggle();
    }
  }

  onInnerClick(event) {
    if (event.target.closest('.drawer-overlay, .cart-drawer__close')) {
      this.close();
      return;
    }

    const noteLabel = event.target.closest('.cart-note label');
    if (noteLabel) noteLabel.classList.toggle('note-open');
  }

  onCartChange(event) {
    const html = event.detail.sections?.[this.sectionId];
    if (html) this.render(html, event.detail.action);
    if (event.detail.action === 'add') this.open();
  }

  /**
   * Rebuy's bundle builder adds to the cart with its own requests, bypassing window.Cart,
   * so no 'cart:change' fires and the drawer neither updates nor opens. Re-sync and open
   * once its adds settle. Only on pages with a bundle builder, and only while the drawer is
   * closed: the cart cross-sell adds from inside the open drawer and the PDP cross-sell
   * re-syncs itself, so neither should trigger a second refresh here.
   */
  onRebuyAdd() {
    if (this.classList.contains('cart-open')) return;
    if (!document.querySelector('[class*="rebuy-bundle-builder"]')) return;

    window.clearTimeout(this.rebuyAddTimer);
    this.rebuyAddTimer = window.setTimeout(async () => {
      // Focus is still on the Rebuy control behind the overlay: move it into the drawer,
      // and return it to that control when the drawer closes
      const opener = document.activeElement;
      try {
        await window.Cart?.refresh();
      } catch (error) {
        console.error(error);
      }
      this.open();
      this.returnFocusTo = opener instanceof HTMLElement && opener !== document.body ? opener : null;
      this.querySelector('.cart-drawer__close')?.focus();
    }, REBUY_ADD_SETTLE_MS);
  }

  /**
   * Animate free-shipping fill from a start width to the rendered target width.
   * @param {string} [fromWidth]
   */
  animateShippingProgress(fromWidth) {
    const fill = this.querySelector('.cart-shipping__fill');
    if (!fill) return;

    const toWidth = fill.style.width || '0%';
    const startWidth = fromWidth ?? '0%';

    fill.style.transition = 'none';
    fill.style.width = startWidth;
    // Force reflow so the browser registers the start width before transitioning.
    void fill.offsetWidth;
    fill.style.transition = '';
    fill.style.width = toWidth;
  }

  render(html, action) {
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('#cart-drawer .drawer__wrapper');
    const current = this.querySelector('.drawer__wrapper');
    if (!next || !current) return;

    const prevFill = current.querySelector('.cart-shipping__fill');
    const fromWidth = prevFill?.style.width || '0%';
    const scrollTop = current.querySelector('.cart-items')?.scrollTop || 0;
    current.innerHTML = next.innerHTML;

    const items = current.querySelector('.cart-items');
    if (items) items.scrollTop = action === 'add' ? 0 : scrollTop;

    this.animateShippingProgress(fromWidth);
  }
}
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
